/**
 * One quest row on the weekly pitch (specs/home-page AC-22, AC-24): a done / active / pending node and
 * the task title; a guest-locked task shows a lock and taps through to sign-up.
 */

import React, { useCallback } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Check, Lock } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import type { Theme } from '../../../src/theme/getTheme';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { WeeklyQuestTaskView } from '../../../src/home/types/home.types';
import type { WeeklyQuestCardStyles } from './WeeklyQuestCard.styles';

export type QuestNodeState = 'done' | 'active' | 'pending';

const NODE_ICON_SIZE = 13;

export interface QuestTrackNodeProps {
  task: WeeklyQuestTaskView;
  state: QuestNodeState;
  /** Guest cannot do this task: lock glyph, tap → sign-up path. */
  locked: boolean;
  onLockedPress: (taskKey: string) => void;
  styles: WeeklyQuestCardStyles;
  theme: Theme;
}

export const QuestTrackNode: React.FC<QuestTrackNodeProps> = ({ task, state, locked, onLockedPress, styles, theme }) => {
  const handlePress = useCallback(() => onLockedPress(task.key), [onLockedPress, task.key]);
  const testID = `${HOME_TEST_IDS.weeklyNode}-${task.key}-${locked ? 'locked' : state}`;

  const nodeStyle = [
    styles.node,
    state === 'done' && styles.nodeDone,
    state === 'active' && styles.nodeActive,
    state === 'pending' && styles.nodePending,
  ];

  const renderNodeContent = () => {
    if (state === 'done') return <Check size={NODE_ICON_SIZE} color={theme.background.inset} strokeWidth={3.5} />;
    if (locked) return <Lock size={NODE_ICON_SIZE} color={theme.gray[500]} strokeWidth={2.4} />;
    return (
      <Text style={[styles.nodeRatio, state === 'active' ? styles.nodeRatioActive : styles.nodeRatioPending]}>
        {task.progress}/{task.target}
      </Text>
    );
  };

  const content = (
    <>
      <View style={nodeStyle}>{renderNodeContent()}</View>
      <Text style={[styles.title, state === 'done' && styles.titleDone]} numberOfLines={2}>
        {task.title}
      </Text>
    </>
  );

  if (locked && state !== 'done') {
    return (
      <Pressable
        style={({ pressed, hovered }) => [
          styles.row,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.rowPressed,
          hovered && Platform.OS === 'web' && styles.rowHover,
        ]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={HOME_COPY.WEEKLY_LOCKED_A11Y}
        testID={testID}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View style={styles.row} testID={testID}>
      {content}
    </View>
  );
};
